import { useParams } from "react-router";

import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { ProcedureNodeStepComment } from "@/features/procedures/model/procedure/ProcedureNodeStepComment";
import { Checklist } from "@/features/procedures/model/types/checklist";
import { StepStatus } from "@/features/procedures/model/types/checklist";
import { useQuery } from "@tanstack/react-query";

import { ProcedureQueryKeys } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

export const useFetchChecklist = (
	checklistId: number,
	procedureId?: number,
) => {
	const { id } = useParams<RouteParams>();
	procedureId = procedureId ?? id;

	return useQuery<Checklist, any>({
		queryKey: [ChecklistQueryKeys.checklist, +checklistId],
		queryFn: () => SATOSPortal.GetChecklist(procedureId, +checklistId),
		enabled: !!checklistId && !!procedureId,
		staleTime: 60000, // Data won't be considered stale for 1 minute
	});
};

export const useFetchChecklistStepStatuses = (
	checklistId: number | undefined,
	procedureId: number,
) => {
	return useQuery<StepStatus[], any>({
		queryKey: [
			ChecklistQueryKeys.checklist,
			+checklistId!,
			ChecklistQueryKeys.status,
		],
		queryFn: () =>
			SATOSPortal.GetChecklistStepStatuses(procedureId, checklistId),
		enabled: !!procedureId && !!checklistId,
		refetchInterval: 4000,
	});
};

export const useFetchCommentsByChecklistId = (checklistId: number) => {
	const { id } = useParams<RouteParams>();
	const procedureId = parseInt(id);
	return useQuery<Record<number, ProcedureNodeStepComment[]>, any>({
		queryKey: [
			ChecklistQueryKeys.checklist,
			checklistId,
			ChecklistQueryKeys.comments,
		],
		queryFn: () =>
			SATOSPortal.GetCommentsByChecklistId(procedureId, checklistId),
		enabled: Boolean(checklistId && procedureId),
	});
};

export const useFetchMaterialHandlingChecklistForEdit = (
	checklistId: number | undefined,
	procedureId: number,
) => {
	return useQuery<Checklist, any>({
		queryKey: [
			ProcedureQueryKeys.materialHandlingChecklistForEdit,
			procedureId,
			checklistId,
		],
		queryFn: () =>
			SATOSPortal.GetMaterialHandlingChecklistForEdit(procedureId, checklistId),
		enabled: !!procedureId && !!checklistId,
	});
};

export const ChecklistQueryKeys = {
	checklist: "checklist",
	checklistPreview: "editorContent",
	comments: "comments",
	status: "status",
};
